// src/components/auth/AuthLayout.jsx
// Shared split-screen layout for login/register pages

import React from "react";
import BubbleBackground from "../common/BubbleBackground";
import Logo from "../common/Logo";

const stats = [
  { value: "500+", label: "Internships Posted" },
  { value: "12k+", label: "Students Placed"   },
  { value: "98%",  label: "Satisfaction Rate"  },
];

const AuthLayout = ({ children, title, subtitle, illustrationTitle, illustrationSubtitle }) => {
  return (
    <div className="min-h-screen flex items-stretch relative">
      <BubbleBackground />

      {/* ---- Left Panel: Branding / Illustration ---- */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-[44%] flex-col relative overflow-hidden">
        {/* Gradient overlay */}
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(145deg, rgba(26,125,232,0.92) 0%, rgba(10,69,160,0.96) 55%, rgba(4,26,82,0.98) 100%)",
          }}
        />

        {/* 3D Bubble Button decorations — sides */}
        {/* Top-right large bubble */}
        <div
          className="absolute top-[-10%] right-[-8%] w-72 h-72 rounded-full z-10 animate-float-slow"
          style={{
            background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.38) 0%, rgba(180,215,255,0.18) 55%, rgba(26,125,232,0.08) 100%)",
            boxShadow: [
              "0 12px 40px rgba(26,125,232,0.22)",
              "0 2px 8px rgba(255,255,255,0.60) inset",
              "0 -4px 14px rgba(26,125,232,0.14) inset",
            ].join(", "),
            border: "1.5px solid rgba(255,255,255,0.40)",
            animationDelay: "0s",
          }}
        />
        {/* Bottom-left large bubble */}
        <div
          className="absolute bottom-[-6%] left-[-6%] w-56 h-56 rounded-full z-10 animate-float"
          style={{
            background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.30) 0%, rgba(180,215,255,0.14) 55%, rgba(26,125,232,0.06) 100%)",
            boxShadow: [
              "0 10px 32px rgba(26,125,232,0.20)",
              "0 2px 6px rgba(255,255,255,0.55) inset",
              "0 -3px 10px rgba(26,125,232,0.12) inset",
            ].join(", "),
            border: "1.5px solid rgba(255,255,255,0.35)",
            animationDelay: "2s",
          }}
        />
        {/* Mid-left small bubble */}
        <div
          className="absolute top-[45%] left-[-5%] w-32 h-32 rounded-full z-10 animate-float-fast"
          style={{
            background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.28) 0%, rgba(180,215,255,0.12) 60%, rgba(26,125,232,0.05) 100%)",
            boxShadow: [
              "0 6px 20px rgba(26,125,232,0.18)",
              "0 1.5px 5px rgba(255,255,255,0.50) inset",
              "0 -2px 8px rgba(26,125,232,0.10) inset",
            ].join(", "),
            border: "1.5px solid rgba(255,255,255,0.32)",
            animationDelay: "1s",
          }}
        />
        {/* Right-side tiny bubble */}
        <div
          className="absolute bottom-[20%] right-[-3%] w-20 h-20 rounded-full z-10 animate-float"
          style={{
            background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.35) 0%, rgba(180,215,255,0.15) 60%, rgba(26,125,232,0.07) 100%)",
            boxShadow: [
              "0 5px 16px rgba(26,125,232,0.20)",
              "0 1.5px 4px rgba(255,255,255,0.55) inset",
              "0 -2px 6px rgba(26,125,232,0.10) inset",
            ].join(", "),
            border: "1.5px solid rgba(255,255,255,0.38)",
            animationDelay: "3.5s",
          }}
        />
        {/* Mid-right extra bubble */}
        <div
          className="absolute top-[25%] right-[-4%] w-16 h-16 rounded-full z-10 animate-float-slow"
          style={{
            background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.32) 0%, rgba(180,215,255,0.12) 60%, rgba(26,125,232,0.06) 100%)",
            boxShadow: [
              "0 4px 14px rgba(26,125,232,0.18)",
              "0 1px 4px rgba(255,255,255,0.50) inset",
            ].join(", "),
            border: "1.5px solid rgba(255,255,255,0.35)",
            animationDelay: "5s",
          }}
        />

        {/* Content */}
        <div className="relative z-20 flex flex-col justify-between h-full p-10 xl:p-14">
          {/* Logo — 3D bubble, image only, white background */}
          <div className="animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            <Logo size="md" />
          </div>

          {/* Center illustration text */}
          <div className="flex-1 flex flex-col items-start justify-center gap-6">
            <div
              className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase animate-fade-in-up"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
                color: "rgba(255,255,255,0.9)",
                animationDelay: "0.2s",
              }}
            >
              Quinternship Platform
            </div>

            <h2
              className="text-4xl xl:text-5xl font-display font-bold text-white leading-tight animate-fade-in-up"
              style={{ animationDelay: "0.3s" }}
            >
              {illustrationTitle || "Launch Your\nCareer Journey"}
            </h2>
            <p
              className="text-base text-blue-100 leading-relaxed max-w-sm animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              {illustrationSubtitle ||
                "Connect with top companies, discover internship opportunities, and build real-world experience with QuBright's curated platform."}
            </p>

            {/* Stats */}
            <div
              className="flex gap-6 mt-2 animate-fade-in-up"
              style={{ animationDelay: "0.5s" }}
            >
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-display font-bold text-white">{s.value}</div>
                  <div className="text-xs text-blue-200 font-medium mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom caption */}
          <p
            className="text-xs text-blue-200 font-medium animate-fade-in"
            style={{ animationDelay: "0.6s" }}
          >
            QuBright Technologies LLP &copy; {new Date().getFullYear()} &mdash; All rights reserved
          </p>
        </div>
      </div>

      {/* ---- Right Panel: Form ---- */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10 p-4 sm:p-8 lg:p-12 min-h-screen">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8 animate-fade-in-up">
          <Logo size="md" />
        </div>

        {/* Glass card */}
        <div
          className="w-full max-w-md animate-fade-in-up"
          style={{ animationDelay: "0.15s" }}
        >
          <div
            className="rounded-3xl p-8 sm:p-10"
            style={{
              background: "rgba(255,255,255,0.75)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              border: "1.5px solid rgba(255,255,255,0.65)",
              boxShadow:
                "0 24px 64px 0 rgba(26,125,232,0.14), 0 2px 16px 0 rgba(255,255,255,0.60) inset",
            }}
          >
            {/* Card 3D bubble decorations */}
            <div
              className="absolute -top-7 -right-7 w-24 h-24 rounded-full pointer-events-none animate-float"
              style={{
                background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.95) 0%, #dceeff 60%, #b8d9ff 100%)",
                boxShadow: [
                  "0 8px 24px rgba(26,125,232,0.22)",
                  "0 2px 6px rgba(255,255,255,0.85) inset",
                  "0 -3px 10px rgba(26,125,232,0.12) inset",
                ].join(", "),
                border: "1.5px solid rgba(255,255,255,0.85)",
                animationDelay: "1s",
              }}
            />
            <div
              className="absolute -bottom-5 -left-5 w-16 h-16 rounded-full pointer-events-none animate-float-fast"
              style={{
                background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.92) 0%, #dceeff 60%, #b8d9ff 100%)",
                boxShadow: [
                  "0 6px 18px rgba(26,125,232,0.20)",
                  "0 2px 4px rgba(255,255,255,0.80) inset",
                  "0 -2px 8px rgba(26,125,232,0.10) inset",
                ].join(", "),
                border: "1.5px solid rgba(255,255,255,0.82)",
                animationDelay: "2.5s",
              }}
            />

            {/* Header */}
            <div className="mb-7 relative">
              <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-800 mb-1.5">
                {title}
              </h1>
              <p className="text-sm text-slate-500 font-medium">{subtitle}</p>
            </div>

            {children}
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-slate-400 mt-6 font-medium">
            Secured by <span className="text-primary-500 font-semibold">QuBright Technologies LLP</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
